/**
 * Browser-side helpers for image embedding JSON (base64 Float32 vectors from aiImageClusterEmbeddings.js).
 *
 * Similarity convention: every helper that RETURNS a similarity (neighbor maps, edges, cluster
 * rankings) returns the unit similarity (1 + cosine) / 2 ∈ [0, 1], so colors, bands and displayed
 * values share one scale. Raw cosine ∈ [-1, 1] is only used internally (and for angular distance).
 */

import { cosineToUnit } from '../common/SimilarityBadge.jsx';

export { cosineToUnit };

export function decodeEmbeddingBase64(b64) {
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  const floats = new Float32Array(bytes.buffer, 0, len / 4);
  return Array.from(floats);
}

// Assumes a and b are unit-normalized vectors.
export function cosineSimilarity(a, b) {
  let dot = 0;
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) {
    dot += a[i] * b[i];
  }
  return dot;
}

/** 1 - cosine similarity (0 = identical direction, 2 = opposite). */
export function cosineDistance(a, b) {
  return 1 - cosineSimilarity(a, b);
}

/** Normalized angular distance in [0, 1]. */
export function angularDistance(a, b) {
  const cos = Math.max(-1, Math.min(1, cosineSimilarity(a, b)));
  return Math.acos(cos) / Math.PI;
}

function dot(a, b) {
  let s = 0;
  for (let i = 0; i < a.length; i++) s += a[i] * b[i];
  return s;
}

function matVec(G, v) {
  const n = G.length;
  const out = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) out[i] += G[i][j] * v[j];
  }
  return out;
}

function normalize(v) {
  let s = 0;
  for (const x of v) s += x * x;
  s = Math.sqrt(s);
  if (s === 0) return v.map(() => 0);
  return v.map(x => x / s);
}

function outerDeflate(G, v, lambda) {
  const n = G.length;
  const out = G.map(row => row.slice());
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      out[i][j] -= lambda * v[i] * v[j];
    }
  }
  return out;
}

/**
 * Largest eigenpair of symmetric matrix (power iteration).
 */
function largestEigenpair(G, iterations = 400) {
  const n = G.length;
  let v = Array(n)
    .fill(0)
    .map(() => Math.random() - 0.5);
  v = normalize(v);
  for (let iter = 0; iter < iterations; iter++) {
    v = normalize(matVec(G, v));
  }
  const Av = matVec(G, v);
  let lambda = 0;
  for (let i = 0; i < n; i++) lambda += v[i] * Av[i];
  return { v, lambda };
}

function buildGramMatrix(centeredRows) {
  const n = centeredRows.length;
  const G = Array(n)
    .fill(0)
    .map(() => Array(n).fill(0));
  for (let i = 0; i < n; i++) {
    for (let j = i; j < n; j++) {
      const d = dot(centeredRows[i], centeredRows[j]);
      G[i][j] = G[j][i] = d;
    }
  }
  return G;
}

function centerColumns(vectors) {
  const n = vectors.length;
  if (n === 0) return [];
  const d = vectors[0].length;
  const mean = Array(d).fill(0);
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < d; j++) mean[j] += vectors[i][j];
  }
  for (let j = 0; j < d; j++) mean[j] /= n;
  return vectors.map(row => row.map((x, j) => x - mean[j]));
}

/**
 * PCA 2D coordinates via eigendecomposition of Gram matrix X X^T (good when n << d).
 *
 * For datasets larger than `sampleLimit`, principal components are fit on a uniform
 * random subsample of that size and every vector is projected onto them — keeping
 * the cubic/quadratic phase O(sampleLimit² · d) regardless of n.
 *
 * @returns {{ coords: [number, number][], varianceExplained: [number, number] }}
 */
export function pca2dFromVectors(vectors, sampleLimit = 300) {
  const n = vectors.length;
  if (n === 0) return { coords: [], varianceExplained: [0, 0] };
  if (n === 1) return { coords: [[0, 0]], varianceExplained: [1, 0] };

  if (n <= sampleLimit) {
    const centered = centerColumns(vectors);
    const G = buildGramMatrix(centered);
    const trace = G.reduce((s, row, i) => s + row[i], 0);

    const { v: v1, lambda: l1 } = largestEigenpair(G);
    const s1 = Math.sqrt(Math.max(0, l1));
    const coords1 = v1.map(x => x * s1);

    const G2 = outerDeflate(G, v1, l1);
    const { v: v2, lambda: l2 } = largestEigenpair(G2);
    const s2 = Math.sqrt(Math.max(0, l2));
    const coords2 = v2.map(x => x * s2);

    const coords = [];
    for (let i = 0; i < n; i++) coords.push([coords1[i], coords2[i]]);

    const v1e = trace > 0 ? l1 / trace : 0;
    const v2e = trace > 0 ? l2 / trace : 0;
    return { coords, varianceExplained: [v1e, v2e] };
  }

  // Sampled path: fit PCs on a subsample, project everyone.
  const d = vectors[0].length;
  const sampleSet = new Set();
  while (sampleSet.size < sampleLimit) sampleSet.add(Math.floor(Math.random() * n));
  const sampleIdxs = Array.from(sampleSet);

  const mean = new Float64Array(d);
  for (const i of sampleIdxs) {
    const v = vectors[i];
    for (let k = 0; k < d; k++) mean[k] += v[k];
  }
  for (let k = 0; k < d; k++) mean[k] /= sampleLimit;

  const sampleCentered = sampleIdxs.map(i => {
    const v = vectors[i];
    const out = new Float64Array(d);
    for (let k = 0; k < d; k++) out[k] = v[k] - mean[k];
    return out;
  });

  const m = sampleLimit;
  const G = Array(m).fill(0).map(() => Array(m).fill(0));
  for (let i = 0; i < m; i++) {
    for (let j = i; j < m; j++) {
      let s = 0;
      const a = sampleCentered[i];
      const b = sampleCentered[j];
      for (let k = 0; k < d; k++) s += a[k] * b[k];
      G[i][j] = G[j][i] = s;
    }
  }
  const trace = G.reduce((s, row, i) => s + row[i], 0);

  const { v: v1, lambda: l1 } = largestEigenpair(G);
  const G2 = outerDeflate(G, v1, l1);
  const { v: v2, lambda: l2 } = largestEigenpair(G2);

  // PC vectors in d-space: w_i = X_sample_centered^T · v_i / sqrt(lambda_i)
  const s1 = Math.sqrt(Math.max(0, l1));
  const s2 = Math.sqrt(Math.max(0, l2));
  const w1 = new Float64Array(d);
  const w2 = new Float64Array(d);
  for (let i = 0; i < m; i++) {
    const sc = sampleCentered[i];
    const c1 = v1[i];
    const c2 = v2[i];
    for (let k = 0; k < d; k++) {
      w1[k] += c1 * sc[k];
      w2[k] += c2 * sc[k];
    }
  }
  if (s1 > 0) for (let k = 0; k < d; k++) w1[k] /= s1;
  if (s2 > 0) for (let k = 0; k < d; k++) w2[k] /= s2;

  // Single linear projection over all N points.
  const coords = new Array(n);
  for (let i = 0; i < n; i++) {
    const v = vectors[i];
    let p1 = 0, p2 = 0;
    for (let k = 0; k < d; k++) {
      const x = v[k] - mean[k];
      p1 += x * w1[k];
      p2 += x * w2[k];
    }
    coords[i] = [p1, p2];
  }

  const v1e = trace > 0 ? l1 / trace : 0;
  const v2e = trace > 0 ? l2 / trace : 0;
  return { coords, varianceExplained: [v1e, v2e] };
}

/**
 * Projects all `rows` onto their single leading principal component, returning one scalar per row.
 * `rows` must already be centered (mean ≈ 0). Uses the Gram-matrix / power-iteration path for
 * n ≤ sampleLimit (then the eigenvector itself, scaled by √λ, IS the projection), and a sampled
 * fit-then-project for larger n — mirroring pca2dFromVectors so the y-axis stays cheap.
 */
function leadingPcCoords1d(rows, sampleLimit = 300) {
  const n = rows.length;
  if (n < 2) return new Array(n).fill(0);
  const d = rows[0].length;

  if (n <= sampleLimit) {
    const G = buildGramMatrix(rows);
    const { v, lambda } = largestEigenpair(G);
    const s = Math.sqrt(Math.max(0, lambda));
    return rows.map((_, i) => v[i] * s);
  }

  const sampleSet = new Set();
  while (sampleSet.size < sampleLimit) sampleSet.add(Math.floor(Math.random() * n));
  const sample = Array.from(sampleSet).map(i => rows[i]);
  const G = buildGramMatrix(sample);
  const { v, lambda } = largestEigenpair(G);
  const s = Math.sqrt(Math.max(0, lambda));
  const w = new Float64Array(d); // PC direction in d-space: Xₛᵀ·v / √λ
  for (let i = 0; i < sample.length; i++) {
    const sc = sample[i];
    const c = v[i];
    for (let k = 0; k < d; k++) w[k] += c * sc[k];
  }
  if (s > 0) for (let k = 0; k < d; k++) w[k] /= s;
  return rows.map((r) => {
    let p = 0;
    for (let k = 0; k < d; k++) p += r[k] * w[k];
    return p;
  });
}

/** Mann–Whitney AUC = P(a random `pos` score outranks a random `neg` score); ties count as 0.5. */
function mannWhitneyAuc(pos, neg) {
  const n1 = pos.length;
  const n2 = neg.length;
  if (!n1 || !n2) return null;
  const all = pos.map(v => ({ v, pos: true })).concat(neg.map(v => ({ v, pos: false })));
  all.sort((a, b) => a.v - b.v);
  // Average-rank ties so a degenerate (all-equal) projection scores exactly 0.5, not a spurious 1.
  for (let i = 0; i < all.length;) {
    let j = i;
    while (j + 1 < all.length && all[j + 1].v === all[i].v) j++;
    const avgRank = (i + j + 2) / 2; // mean of 1-based ranks i+1..j+1
    for (let t = i; t <= j; t++) all[t].rank = avgRank;
    i = j + 1;
  }
  let rankSumPos = 0;
  for (const o of all) if (o.pos) rankSumPos += o.rank;
  return (rankSumPos - (n1 * (n1 + 1)) / 2) / (n1 * n2);
}

/**
 * Held-out separability of two groups: fit the difference-of-means axis on one half (even
 * positions within each group), project the other half onto it, and score with Mann–Whitney AUC.
 * Group A is the positive class (it projects high since w = μA,train − μB,train). Returns null if
 * any train/test half is empty. AUC ≈ 0.5 ⇒ indistinguishable (treat as one cluster); ≈ 1 ⇒ clean
 * split. Because the axis is fit on disjoint data, this self-corrects the "optimized the projection"
 * bias that makes the in-sample Fisher J look significant even for a single cluster.
 */
function heldOutSeparabilityAuc(vectors, idxA, idxB, gMean) {
  const trainA = [], testA = [], trainB = [], testB = [];
  idxA.forEach((i, p) => (p % 2 ? testA : trainA).push(i));
  idxB.forEach((i, p) => (p % 2 ? testB : trainB).push(i));
  if (!trainA.length || !testA.length || !trainB.length || !testB.length) return null;

  const d = vectors[0].length;
  const meanOf = (idxs) => {
    const m = new Float64Array(d);
    for (const i of idxs) { const v = vectors[i]; for (let k = 0; k < d; k++) m[k] += v[k]; }
    for (let k = 0; k < d; k++) m[k] /= idxs.length;
    return m;
  };
  const mA = meanOf(trainA);
  const mB = meanOf(trainB);
  const w = new Float64Array(d);
  for (let k = 0; k < d; k++) w[k] = mA[k] - mB[k];
  const score = (i) => {
    const v = vectors[i];
    let s = 0;
    for (let k = 0; k < d; k++) s += (v[k] - gMean[k]) * w[k];
    return s;
  };
  return mannWhitneyAuc(testA.map(score), testB.map(score));
}

/**
 * Discriminant 2D layout for TWO labeled groups — the projection that shows their latent difference
 * most clearly (vs pca2dFromVectors, which maximizes total variance and ignores the labels).
 *
 *   x = the Fisher difference-of-means axis ŵ = (μA − μB)/‖μA − μB‖ — the direction of greatest
 *       between-cluster gap (LDA with an isotropic within-class covariance; robust when n ≪ d, where
 *       a full S_w⁻¹ would be singular). Group A sits on the high-x side.
 *   y = the leading PCA direction of the residual AFTER removing the x component, so it shows the
 *       remaining spread orthogonal to the separation axis.
 *
 * Also returns two separability scores:
 *   fisherJ    — in-sample (μA−μB)² / (varA + varB) on the x axis: the gap in units of within-cluster
 *                spread. Higher ⇒ better separated. Optimistic (the axis is fit to these same points).
 *   aucHeldOut — honest held-out AUC (see heldOutSeparabilityAuc); ≈ 0.5 ⇒ the two are effectively
 *                the same cluster, ≈ 1 ⇒ genuinely distinct.
 *
 * Falls back to plain PCA (with fallback:true, fisherJ:0, aucHeldOut:null) when the labels don't
 * describe exactly two non-empty groups.
 *
 * @param {number[][]} vectors
 * @param {Array<*>} labels  one per vector; the two distinct values define groups A (first seen) and B.
 * @returns {{ coords: [number,number][], fisherJ: number, aucHeldOut: number|null, fallback: boolean }}
 */
export function discriminant2dFromVectors(vectors, labels) {
  const n = vectors.length;
  if (n === 0) return { coords: [], fisherJ: 0, aucHeldOut: null, fallback: false };
  const d = vectors[0].length;

  const keys = labels.map(String);
  const uniq = [];
  for (const k of keys) if (!uniq.includes(k)) { uniq.push(k); if (uniq.length > 2) break; }

  const idxA = [], idxB = [];
  for (let i = 0; i < n; i++) (keys[i] === uniq[0] ? idxA : idxB).push(i);

  if (uniq.length !== 2 || !idxA.length || !idxB.length) {
    return { ...pca2dFromVectors(vectors), fisherJ: 0, aucHeldOut: null, fallback: true };
  }

  const meanOf = (idxs) => {
    const m = new Float64Array(d);
    for (const i of idxs) { const v = vectors[i]; for (let k = 0; k < d; k++) m[k] += v[k]; }
    for (let k = 0; k < d; k++) m[k] /= idxs.length;
    return m;
  };
  const muA = meanOf(idxA);
  const muB = meanOf(idxB);
  const gMean = meanOf(Array.from({ length: n }, (_, i) => i));

  // Unit discriminant direction ŵ = (μA − μB) / ‖μA − μB‖.
  const wHat = new Float64Array(d);
  let wn = 0;
  for (let k = 0; k < d; k++) { wHat[k] = muA[k] - muB[k]; wn += wHat[k] * wHat[k]; }
  wn = Math.sqrt(wn);
  if (wn > 0) for (let k = 0; k < d; k++) wHat[k] /= wn;

  // x = projection onto ŵ; residual = centered point with its ŵ component removed (drives the y PC).
  const xs = new Array(n);
  const residual = new Array(n);
  for (let i = 0; i < n; i++) {
    const v = vectors[i];
    let proj = 0;
    const c = new Float64Array(d);
    for (let k = 0; k < d; k++) { const cv = v[k] - gMean[k]; c[k] = cv; proj += cv * wHat[k]; }
    xs[i] = proj;
    const r = new Float64Array(d);
    for (let k = 0; k < d; k++) r[k] = c[k] - proj * wHat[k];
    residual[i] = r;
  }
  const ys = leadingPcCoords1d(residual);

  const coords = new Array(n);
  for (let i = 0; i < n; i++) coords[i] = [xs[i], ys[i]];

  // In-sample Fisher ratio on the x axis: squared mean gap over pooled within-group variance.
  const stats = (idxs) => {
    let mean = 0;
    for (const i of idxs) mean += xs[i];
    mean /= idxs.length;
    let varr = 0;
    for (const i of idxs) { const dlt = xs[i] - mean; varr += dlt * dlt; }
    varr /= Math.max(1, idxs.length - 1);
    return { mean, varr };
  };
  const a = stats(idxA);
  const b = stats(idxB);
  const gap = a.mean - b.mean;
  const fisherJ = a.varr + b.varr > 0 ? (gap * gap) / (a.varr + b.varr) : 0;

  const aucHeldOut = heldOutSeparabilityAuc(vectors, idxA, idxB, gMean);

  return { coords, fisherJ, aucHeldOut, fallback: false };
}

function nearestCentroid(point, centroids) {
  let best = 0;
  let bestD = Infinity;
  for (let c = 0; c < centroids.length; c++) {
    const dx = point[0] - centroids[c][0];
    const dy = point[1] - centroids[c][1];
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = c; }
  }
  return best;
}

/**
 * k-means on 2D points. Returns cluster id 0..k-1 per point.
 *
 * For datasets larger than `sampleLimit`, centroids are fit on a uniform random
 * subsample of that size and then every point is labeled by its nearest centroid.
 * Keeps the iterative phase O(sampleLimit · k · maxIter) regardless of n.
 */
export function kmeans2d(points, k, maxIter = 80, sampleLimit = 300) {
  const n = points.length;
  k = Math.max(1, Math.min(k, n));
  if (n === 0) return [];
  if (k === 1) return Array(n).fill(0);

  let trainingPoints = points;
  if (n > sampleLimit) {
    const idxs = new Set();
    while (idxs.size < sampleLimit) idxs.add(Math.floor(Math.random() * n));
    trainingPoints = Array.from(idxs).map(i => points[i]);
  }
  const trainN = trainingPoints.length;

  const centroids = [];
  const used = new Set();
  for (let c = 0; c < k; c++) {
    let idx = Math.floor(Math.random() * trainN);
    let guard = 0;
    while (used.has(idx) && guard++ < trainN * 2) idx = Math.floor(Math.random() * trainN);
    used.add(idx);
    centroids.push(trainingPoints[idx].slice());
  }

  const trainLabels = Array(trainN).fill(0);

  for (let iter = 0; iter < maxIter; iter++) {
    let changed = false;
    for (let i = 0; i < trainN; i++) {
      const best = nearestCentroid(trainingPoints[i], centroids);
      if (trainLabels[i] !== best) { trainLabels[i] = best; changed = true; }
    }

    const counts = Array(k).fill(0);
    const sums = Array(k).fill(0).map(() => [0, 0]);
    for (let i = 0; i < trainN; i++) {
      const c = trainLabels[i];
      counts[c]++;
      sums[c][0] += trainingPoints[i][0];
      sums[c][1] += trainingPoints[i][1];
    }
    for (let c = 0; c < k; c++) {
      if (counts[c] > 0) {
        centroids[c][0] = sums[c][0] / counts[c];
        centroids[c][1] = sums[c][1] / counts[c];
      }
    }

    if (!changed) break;
  }

  // Single pass over all points to produce the full labeling.
  const labels = Array(n);
  for (let i = 0; i < n; i++) labels[i] = nearestCentroid(points[i], centroids);
  return labels;
}

/**
 * Color bands used for nearest-neighbor highlighting, in UNIT similarity (1 + cosine) / 2 ∈ [0, 1]
 * (e.g. the 0.98 band = raw cosine 0.96). Higher similarity ⇒ greener; lower similarity ⇒ redder.
 */
export const SIMILARITY_BANDS = [
  { min: 0.98, color: '#16a34a', label: '> 0.98' },
  { min: 0.95, color: '#84cc16', label: '> 0.95' },
  { min: 0.90, color: '#facc15', label: '> 0.90' },
  { min: 0.85, color: '#f97316', label: '> 0.80' },
  { min: -Infinity, color: '#ef444422', label: '≤ 0.80' },
];

export function similarityColor(sim) {
  for (const band of SIMILARITY_BANDS) {
    if (sim > band.min) return band.color;
  }
  return SIMILARITY_BANDS[SIMILARITY_BANDS.length - 1].color;
}

/**
 * Returns the indices of the top-N images closest (by cosine similarity) to `items[selectedIndex]`,
 * along with their rank (1..N) and unit similarity ∈ [0, 1]. Excludes the selected index itself.
 */
export function topNeighborsBySimilarity(items, selectedIndex, n = 10) {
  if (selectedIndex == null || !items[selectedIndex]) return new Map();
  const sel = items[selectedIndex];
  const sorted = items
    .map((it, idx) => ({ idx, sim: cosineSimilarity(sel.vector, it.vector) }))
    .filter(r => r.idx !== selectedIndex)
    .sort((a, b) => b.sim - a.sim)
    .slice(0, n);
  const map = new Map();
  sorted.forEach((r, i) => map.set(r.idx, { rank: i + 1, sim: cosineToUnit(r.sim) }));
  return map;
}

/**
 * For every item, finds its single nearest neighbor by cosine similarity (i.e.
 * smallest cosine distance). Returns one edge per item: { from, to, sim } with
 * sim the unit similarity ∈ [0, 1].
 * O(n²·dim) — computed once per dataset by the caller, not per render.
 */
export function nearestNeighborEdges(items) {
  const n = items.length;
  if (n < 2) return [];
  const edges = [];
  for (let i = 0; i < n; i++) {
    const vi = items[i].vector;
    let bestIdx = -1;
    let bestSim = -Infinity;
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      const s = cosineSimilarity(vi, items[j].vector);
      if (s > bestSim) { bestSim = s; bestIdx = j; }
    }
    if (bestIdx >= 0) edges.push({ from: i, to: bestIdx, sim: cosineToUnit(bestSim) });
  }
  return edges;
}

/** Index into SIMILARITY_BANDS for a given similarity (mirrors similarityColor selection). */
export function similarityBandIndex(sim) {
  const i = SIMILARITY_BANDS.findIndex(band => sim > band.min);
  return i === -1 ? SIMILARITY_BANDS.length - 1 : i;
}

